import { Dialog } from "@mui/material";
import { Icon } from "@iconify/react";
import { useState } from "react";
import { toast } from "react-toastify";
import { HStack, VStack } from "../../../components/utils";
import { useGetDocumentTemplateVariables } from "../../../queries/esopPlan";
import { TemplateVariableInfo } from "../../../api/Esop";

type Props = {
  open: boolean;
  onClose: () => void;
  templateType: string;
};

const TYPE_STYLES: Record<string, string> = {
  string: "bg-blue-50 text-blue-600",
  number: "bg-green-50 text-green-600",
  boolean: "bg-orange-50 text-orange-600",
  date: "bg-purple-50 text-purple-600",
};

const DESC_MAP: Record<string, string> = {
  // Employee
  "employee.name":
    "Inserts the full legal name of the employee as registered in the organisation",
  "employee.email": "Inserts the official work email address of the employee",
  "employee.id": "Inserts the unique employee ID assigned by the organisation",
  "employee.department":
    "Inserts the department or business unit the employee belongs to",
  "employee.designation": "Inserts the job title or role held by the employee",
  "employee.dateOfJoining":
    "Inserts the date on which the employee joined the organisation",
  "employee.pan": "Inserts the employee's PAN number for tax identification",
  "employee.address":
    "Inserts the registered residential address of the employee",
  // Grant
  "grant.number":
    "Inserts the unique reference number that identifies this specific grant",
  "grant.quantity":
    "Inserts the total number of options or units granted to the employee",
  "grant.dateOfGrant":
    "Inserts the date on which the grant was officially awarded",
  "grant.expiryDate":
    "Inserts the last date by which the granted options must be exercised",
  "grant.strikePrice":
    "Inserts the exercise price per option unit applicable at the time of grant",
  "grant.vestingDate":
    "Inserts the date on which this tranche of options vests",
  "grant.vestedQuantity":
    "Inserts the number of options that have vested as of the letter date",
  // Exercise
  "exercise.quantity":
    "Inserts the number of vested options being exercised in this transaction",
  "exercise.date":
    "Inserts the date on which the employee is exercising the vested options",
  "exercise.amount":
    "Inserts the total monetary amount payable to exercise the selected options",
  "exercise.price":
    "Inserts the per-unit price applicable at the time of exercise",
  // Company
  "company.name":
    "Inserts the registered legal name of the company issuing the options",
  "company.cin":
    "Inserts the Corporate Identification Number assigned by the Registrar of Companies",
  "company.address": "Inserts the registered office address of the company",
  // Plan
  "plan.name":
    "Inserts the name of the ESOP plan under which the grant was issued",
  "plan.startDate": "Inserts the commencement date of the ESOP plan",
};

function getDesc(name: string): string | undefined {
  if (DESC_MAP[name]) return DESC_MAP[name];
  // suffix-pattern fallback — e.g. unknown "foo.name" borrows "employee.name" desc
  const suffix = name.split(".").pop();
  const match = Object.keys(DESC_MAP).find(
    (k) => k.split(".").pop() === suffix
  );
  return match ? DESC_MAP[match] : undefined;
}

function typeBadgeClass(type: string): string {
  return TYPE_STYLES[type.toLowerCase()] ?? "bg-gray-100 text-gray-500";
}

function copyToClipboard(name: string) {
  navigator.clipboard
    .writeText(`{{${name}}}`)
    .then(() => toast.success(`Copied ${name}`))
    .catch(() => toast.error("Failed to copy"));
}

function VariableCard({ variable }: { variable: TemplateVariableInfo }) {
  const description = variable.desc ?? getDesc(variable.name);
  return (
    <button
      type="button"
      onClick={() => copyToClipboard(variable.name)}
      title={`Click to copy {{${variable.name}}}`}
      className="flex items-start justify-between w-full px-3 py-2.5 rounded-lg border border-gray-100 hover:border-indigo-200 hover:bg-indigo-50/40 transition-colors group text-left"
    >
      <VStack className="gap-1 min-w-0 flex-1">
        <HStack className="items-center gap-2 hidden">
          <span className="text-sm font-mono font-medium text-gray-800 truncate">
            {variable.name}
          </span>
        </HStack>
        {description && (
          <HStack className="items-center gap-2">
            <span className="text-xs text-gray-400 leading-snug text-gray-800">
              {description}
            </span>
            <span
              className={`text-xs font-medium px-1.5 py-0.5 rounded shrink-0 ${typeBadgeClass(
                variable.type
              )}`}
            >
              {variable.type}
            </span>
          </HStack>
        )}
        <HStack className="items-center gap-1.5 mt-0.5">
          <Icon
            icon="material-symbols:content-copy-outline"
            width={11}
            className="text-gray-300 group-hover:text-indigo-400 transition-colors"
          />
          <code className="text-xs font-mono text-indigo-400 bg-indigo-50 px-1.5 py-0.5 rounded select-all">
            {`${variable.name}`}
          </code>
        </HStack>
      </VStack>
    </button>
  );
}

function SectionDivider({
  label,
  count,
  icon,
}: {
  label: string;
  count: number;
  icon: string;
}) {
  return (
    <HStack className="items-center gap-2 mt-5 mb-2">
      <Icon icon={icon} className="text-gray-400" width={14} />
      <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
        {label}
      </p>
      <span className="text-xs text-gray-300">({count})</span>
      <div className="flex-1 h-px bg-gray-100 ml-1" />
    </HStack>
  );
}

export default function TemplateVariablesModal({
  open,
  onClose,
  templateType,
}: Props) {
  const [search, setSearch] = useState("");

  const { data, isLoading, isError } = useGetDocumentTemplateVariables(
    open ? templateType : ""
  );

  const q = search.trim().toLowerCase();

  const matchesQuery = (v: { name: string; desc?: string }) =>
    v.name.toLowerCase().includes(q) ||
    (v.desc?.toLowerCase().includes(q) ?? false);

  const filteredVariables =
    data?.variables
      .filter((v) => v.type.toLowerCase() !== "boolean")
      .filter(matchesQuery) ?? [];
  const filteredArrays =
    data?.arrays
      .map((arr) => ({
        ...arr,
        fields:
          arr.fields
            .filter((f) => f.type.toLowerCase() !== "boolean")
            .filter(matchesQuery) || "",
      }))
      .filter(
        (arr) => arr.name.toLowerCase().includes(q) || arr.fields.length > 0
      ) ?? [];
  const filteredSignature =
    data?.signature.filter((v) => v.toLowerCase().includes(q)) ?? [];

  const totalVisible =
    filteredVariables.length +
    filteredArrays.reduce((s, a) => s + a.fields.length, 0) +
    filteredSignature.length;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <VStack className="bg-white rounded-lg overflow-hidden">
        {/* Header */}
        <HStack className="items-start justify-between px-6 pt-6 pb-4 border-b border-gray-100">
          <VStack className="gap-0.5">
            <HStack className="items-center gap-2">
              <Icon
                icon="material-symbols:menu-book-outline"
                className="text-gray-500"
                width={20}
              />
              <p className="text-base font-semibold text-gray-800">
                Template Variables
              </p>
            </HStack>
            <p className="text-xs text-gray-500 ml-7">
              Click any variable to copy its syntax into your clipboard.
            </p>
          </VStack>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <Icon icon="material-symbols:close" width={20} />
          </button>
        </HStack>

        {/* Search */}
        <div className="px-6 pt-4 pb-2">
          <HStack className="items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 bg-gray-50 focus-within:border-indigo-300 focus-within:bg-white transition-colors">
            <Icon
              icon="material-symbols:search"
              className="text-gray-400 shrink-0"
              width={16}
            />
            <input
              type="text"
              placeholder="Search variables..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="flex-1 text-sm bg-transparent outline-none text-gray-700 placeholder-gray-400"
            />
            {search && (
              <button
                onClick={() => setSearch("")}
                className="text-gray-400 hover:text-gray-600"
              >
                <Icon icon="material-symbols:close" width={14} />
              </button>
            )}
          </HStack>
          {search && data && (
            <p className="text-xs text-gray-400 mt-1.5 pl-1">
              {totalVisible} result{totalVisible !== 1 ? "s" : ""}
            </p>
          )}
        </div>

        {/* Body */}
        <VStack className="px-6 pb-5 overflow-y-auto max-h-[420px]">
          {isLoading && (
            <p className="text-sm text-gray-400 text-center py-10">
              Loading variables...
            </p>
          )}
          {isError && (
            <p className="text-sm text-red-500 text-center py-10">
              Failed to load template variables.
            </p>
          )}
          {data && totalVisible === 0 && (
            <p className="text-sm text-gray-400 text-center py-10">
              No variables match &quot;{search}&quot;.
            </p>
          )}
          {data && (
            <>
              {filteredVariables.length > 0 && (
                <VStack>
                  <SectionDivider
                    label="Variables"
                    count={filteredVariables.length}
                    icon="material-symbols:code"
                  />
                  <div className="flex flex-col gap-2">
                    {filteredVariables.map((v) => (
                      <VariableCard key={v.name} variable={v} />
                    ))}
                  </div>
                </VStack>
              )}

              {filteredArrays.length > 0 && (
                <VStack>
                  <SectionDivider
                    label="Arrays"
                    count={filteredArrays.length}
                    icon="material-symbols:list"
                  />
                  {filteredArrays.map((arr) => (
                    <VStack key={arr.name} className="mb-3">
                      <HStack className="items-center gap-1.5 mb-1.5">
                        <Icon
                          icon="material-symbols:table-rows-narrow"
                          className="text-gray-400"
                          width={13}
                        />
                        <span className="text-xs font-semibold text-gray-600">
                          {arr.name}
                        </span>
                      </HStack>
                      <div className="flex flex-col gap-2 pl-4 border-l-2 border-gray-100">
                        {arr.fields.map((f) => (
                          <VariableCard
                            key={`${arr.name}.${f.name}`}
                            variable={f}
                          />
                        ))}
                      </div>
                    </VStack>
                  ))}
                </VStack>
              )}

              {filteredSignature.length > 0 && (
                <VStack>
                  <SectionDivider
                    label="Signature"
                    count={filteredSignature.length}
                    icon="material-symbols:signature"
                  />
                  <div className="flex flex-col gap-2">
                    {filteredSignature.map((v, index) => (
                      <VariableCard
                        key={`Signature-${index}`}
                        variable={{ name: v, type: "string" }}
                      />
                    ))}
                  </div>
                </VStack>
              )}
            </>
          )}
        </VStack>
      </VStack>
    </Dialog>
  );
}
